import tw from 'twin.macro';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsList } from '@teetsh/app/src/components/ItemsList';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAllEDTs } from '@teetsh/app/src/queries/edt/useAllEDTs';
import useEDTs from '@teetsh/app/src/queries/edt/useEDTs';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import useDeleteEDT from '@teetsh/app/src/queries/edt/useDeleteEDT';
import { Menu } from 'components/Menu';
import { EllipsisHorizontalIcon as DotsHorizontal } from '@heroicons/react/24/outline';
import { TrashIcon as Trash } from '@heroicons/react/24/outline';
import { DuplicateIcon } from 'components/Icons/icons/Duplicate';
import { ArrowTopRightOnSquareIcon as ExternalLink } from '@heroicons/react/24/outline';
import { PencilIcon } from '@heroicons/react/24/outline';
import useDuplicateEDT from '@teetsh/app/src/queries/edt/useDuplicateEDT';
import { EDTIllustration } from '../EDTIllustrations';
import { useSchoolGoTo } from '@teetsh/app/src/containers/App/useSchoolGoTo';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { DuplicateSharedEDTModal } from '../EDT/EDTShare/DuplicateSharedEDTModal';
import { CopyEDTModal } from './CopyEDTModal';
import { UpdateEDTInfoModal } from '../UpdateEDTInfoModal';
import { useOpenUpdateEDTInfoModal } from '../UpdateEDTInfoModal/useOpenUpdateEDTInfoModal';

export function EmploiDuTempsList() {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const shareToken = urlSearchParams.get('share-token');
  const shareEDTId = urlSearchParams.get('share-entity-id');
  const [currentEDTIdForList, setCurrentEDTIdForList] = useState<
    string | undefined
  >(undefined);
  const navigate = useNavigate();
  const goTo = useSchoolGoTo();
  const { locale } = useUser();
  const { data, isLoading, isFetched } = useEDTs({ enabled: true });
  const duplicateEDT = useDuplicateEDT();
  const deleteEDT = useDeleteEDT();
  const { school, schoolyear } = useSchoolSchoolyear();
  const { t } = useTranslation(['common', 'emploiDuTemps']);
  const openUpdateModal = useOpenUpdateEDTInfoModal();
  // Not needed here, it just ensures that the data is already in the cache before navigating to the 'New' page
  useAllEDTs();

  if (isLoading || !isFetched) {
    return <SpinnerComponent />;
  }
  const emploiDuTemps = data || [];

  const onDuplicate = edt =>
    duplicateEDT({
      edt,
    });
  const EDTActions = ({ id }: { id: string }) => {
    const edt = emploiDuTemps.find(e => e.id === id);
    if (!edt) {
      return null;
    }

    return (
      <div
        role="presentation"
        tw="flex items-center"
        onClick={e => e.stopPropagation()}
      >
        <Menu portal>
          <Tooltip2 label={t('common:moreOptions')}>
            <Menu.Button
              aria-label="More options"
              minimal
              icon={DotsHorizontal}
            />
          </Tooltip2>
          <Menu.Items size="auto">
            <div tw="py-1">
              <Menu.Item
                icon={PencilIcon}
                text={t('common:buttons.rename')}
                onClick={() =>
                  openUpdateModal({
                    id: edt.id,
                    name: edt.name,
                    subtitle: edt.subtitle,
                  })
                }
                aria-label={`Éditer l'emploi du temps ${edt.name}`}
              />
              <Menu.Item
                icon={ExternalLink}
                text={t('emploiDuTemps:list.tooltip.copyToAnother')}
                onClick={() => setCurrentEDTIdForList(edt.id)}
                aria-label={`Copier l'emploi du temps ${edt.name} sur une autre classe`}
              />
              <Menu.Item
                icon={DuplicateIcon as any}
                text={t('emploiDuTemps:list.tooltip.duplicate')}
                onClick={() => onDuplicate(edt)}
                aria-label={`Dupliquer l'emploi du temps ${edt.name}`}
              />
            </div>
            <div tw="py-1">
              <Menu.Item
                intent="danger"
                icon={Trash}
                text={t('common:buttons.delete')}
                aria-label={`Supprimer l'emploi du temps ${edt.name}`}
                onClick={() => deleteEDT({ edtId: edt.id })}
                withConfirmation
                popoverText={t('emploiDuTemps:list.tooltip.confirmDeletion')}
              />
            </div>
          </Menu.Items>
        </Menu>
      </div>
    );
  };

  return (
    <div css={[emploiDuTemps.length === 0 && tw`h-full`]}>
      {shareToken && shareEDTId && (
        <DuplicateSharedEDTModal
          token={shareToken}
          edtId={shareEDTId}
          locale={locale}
        />
      )}
      <CopyEDTModal
        edtId={currentEDTIdForList}
        onClose={() => setCurrentEDTIdForList(undefined)}
        locale={locale}
      />
      <UpdateEDTInfoModal />
      <ItemsList
        title={t('emploiDuTemps:list.titles.mySchedules')}
        newTitle={t('emploiDuTemps:list.messages.welcome')}
        newDescription={t('emploiDuTemps:list.messages.createInstructions')}
        newButtonCtaLabel={t('emploiDuTemps:list.actions.startNewSchedule')}
        illustration={<EDTIllustration />}
        items={emploiDuTemps.map(edt => ({
          id: edt.id,
          title: edt.name,
          link: `/annee-scolaire/${schoolyear.id}/ecole/${school.id}/emploi-du-temps/${edt.id}`,
          subtitle: edt.subtitle,
        }))}
        onClickItem={id =>
          navigate(
            `/annee-scolaire/${schoolyear.id}/ecole/${school.id}/emploi-du-temps/${id}`,
          )
        }
        onAddItem={() => goTo('emploi-du-temps/new')}
        Actions={EDTActions}
      />
    </div>
  );
}
